import React, { useEffect, useCallback } from 'react';
import styled from 'astroturf/react';

import $image_url from 'js/utils/image_url';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import { getCheckoutUrl } from 'js/liswp/utils';
import { Modal } from 'js/design_system/Modal';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { Icon } from 'js/design_system/Icon';
import { WorkId } from 'js/liswp/types';
import * as Dismissibles from 'js/utils/dismissibles';

type Props = {
  open?: boolean;
  onClose: () => void;
  background?: 'white' | 'gray';
  workId: WorkId;
  trialPricingCopy?: string;
  impressionSource: string;
};

const MOBILE_BREAKPOINT = 860;

const TITLE_ID = 'fallback-bulkdownload-upsell-title';

const DISMISSIBLE_KEY = 'FallbackBulkDownloadUpsellModal';

const StyledModal = styled(Modal)`
  > :global(.ds2-5-modal__container) {
    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      overflow: scroll;
      padding: 22px 8px;
    }
  }
`;

const Title = styled.h1`
  color: #000;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 32px;
  text-align: center;
`;

const Attribution = styled.div`
  display: grid;
  gap: 3px 16px;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
`;

const Avatar = styled.img`
  border-radius: 50%;
  grid-column: 1;
  grid-row: 1 / 3;
  height: 58px;
  width: 58px;
`;

const Button = styled.button<{ free?: boolean; lightBlue?: boolean }>`
  align-items: center;
  align-self: center;
  background: #fff;
  border: 0;
  border-radius: 4px;
  color: #0954d3;
  display: block;
  font-size: 16px;
  font-weight: 700;
  justify-content: center;
  margin-bottom: 31px;
  max-width: 396px;
  padding: 16px 32px;
  text-transform: uppercase;
  text-align: center;
  width: 100%;

  span {
    display: inline-block;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 14px;
    padding: 16px;
  }

  &.lightBlue {
    background: #f0f1f9;
  }

  &.free {
    margin: 0;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      margin: 0 auto;
    }
  }

  &.premium {
    // Left margin is to compensate for asymmetrical padding on the parent container
    margin-left: 10px;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      margin: 0;
      order: 5;
    }
  }

  &:hover {
    color: #0954d3;
    text-decoration: none;
  }
`;

const Container = styled.div`
  display: grid;
  gap: 20px 22px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    background: #fff;
    display: flex;
    flex-direction: column;
    // Chrome doesn't support gap on flex containers, but FF does
    gap: 0;
    padding-bottom: 34px;
  }
`;

const Feature = styled.h3`
  align-self: end;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  margin: 0;

  // With no description, we want the feature heading to take up two rows
  // so it's vertically aligned with the image
  &.noDesc {
    align-self: center;
    grid-row: span 2;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 17px;
  }
`;

const FeatureContainer = styled.div`
  align-items: center;
  display: grid;
  gap: 2px 19px;
  grid-template-columns: 125px 1fr;
  grid-template-rows: auto auto;
  flex-basis: 0;
  flex-grow: 1;
  // This is necessary for the images' max-height to work correctly
  min-height: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 2px 15px;
    grid-template-columns: 100px 1fr;
    flex-basis: auto;
    margin-bottom: 23px;
  }
`;

const FeatureDesc = styled.p`
  align-self: start;
  color: #c9d9f5;
  font-size: 16px;
  grid-column: 2 / 3;
  margin: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 15px;
  }
`;

const FeatureIcon = styled.img`
  grid-row: span 2;
  justify-self: center;
  max-height: 60px;
`;

const FreeContainer = styled.section`
  border: 1px solid #cdd1eb;
  border-radius: 7px;
  grid-column: 1;
  padding: 24px 71px 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    order: 3;
    padding: 19px 10px 18px;
  }
`;

const IconContainer = styled.span`
  margin-left: 14px;
`;

const Name = styled.p`
  align-self: end;
  color: #3b3f45;
  font-size: 16px;
  margin: 0;

  &.institution {
    align-self: start;
    color: #9b9da1;
  }
`;

const PlusText = styled.p`
  align-self: center;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  margin: 37px 0 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 16px 0;
    text-align: center;
  }
`;

const PremiumContainer = styled.section`
  background: #0954d3;
  border-radius: 7px;
  color: #fff;
  display: flex;
  flex-direction: column;
  grid-row: 1 / 3;
  grid-column: 2;
  padding: 24px 16px 40px 6px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    order: 1;
    padding: 16px 10px;
  }
`;

const Quote = styled.blockquote`
  border: 0;
  color: #3b3f45;
  font-size: 22px;
  line-height: 31px;
  margin-bottom: 28px;
  padding: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 18px;
  }
`;

const SectionTitle = styled.h2`
  // So titles are aligned vertically
  align-items: center;
  color: #fff;
  display: flex;
  font-size: 28px;
  font-weight: 700;
  // Hardcoded height so the buttons align correctly
  height: 31px;
  justify-content: center;
  margin: 0 0 29px;
  width: 100%;

  &.free {
    color: #222;
    font-size: 24px;
    margin-bottom: 28px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 20px;
    height: unset;
    margin-bottom: 20px;
  }
`;

const TestimonialContainer = styled.aside`
  background: #f9f9f9;
  border-radius: 7px;
  grid-column: 1;
  padding: 111px 31px 111px 46px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    background: #fff;
    order: 2;
    padding: 24px 20px 36px;
  }
`;

// This upsell modal is a fallback for the bulk download modal.
// We only show the bulk download upsell modal every 10 minutes,
// and 25% of the time, someone downloads a second paper in this
// period and we show them nothing, so that's where this comes in.
const FallbackBulkDownloadUpsellModalControl: React.FunctionComponent<Props> = (
  props: Props,
) => {
  const {
    open = true,
    onClose,
    workId,
    background,
    trialPricingCopy,
    impressionSource,
  } = props;

  const checkoutUrl = getCheckoutUrl(
    workId,
    'ALL_READER_PRODUCTS',
    'swp-sticky-header-work-card-download-button',
  );

  const { screenWidth: width } = useScreenSize();

  useEffect(() => {
    Dismissibles.recordShow(DISMISSIBLE_KEY);
  }, []);

  const handleClose = useCallback(() => {
    Dismissibles.recordDismissal(DISMISSIBLE_KEY);
    onClose();
  }, [onClose]);

  const handleCtaClick = useCallback(() => {
    Dismissibles.recordClickthrough(DISMISSIBLE_KEY);
  }, []);

  return (
    <StyledModal
      open={open}
      onClose={handleClose}
      showHeader={false}
      aria-labelledby={TITLE_ID}
      containerStyle={{ maxWidth: '1140px' }}
    >
      <ImpressionWatcher
        source={impressionSource}
        entity_type={2}
        entity_id={workId}
        extra={{ modalVariant: 'FallbackBulkDownloadUpsellModalControl' }}
        trackDimensions
      >
        <Title id={TITLE_ID}>Choose Your Download</Title>
        <Container>
          <FreeContainer>
            {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
            <SectionTitle free>
              {width > MOBILE_BREAKPOINT ? 'Free' : 'Free Download'}
            </SectionTitle>
            <Button
              free
              lightBlue={background !== 'gray'}
              onClick={handleClose}
            >
              Download this PDF only
            </Button>
          </FreeContainer>

          <TestimonialContainer>
            <Quote>
              "
              <strong>
                I now have an enormous reading library available to peruse or
                search thanks to Bulk Download.
              </strong>{' '}
              The wealth of knowledge and materials you make available through
              this service is actually thrilling."
            </Quote>
            <Attribution>
              <Avatar
                src={$image_url('people/john_ancient_near_east_history.jpg')}
              />
              <Name>John</Name>
              {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
              <Name institution>Ancient Near East Hobbyist</Name>
            </Attribution>
          </TestimonialContainer>

          <PremiumContainer>
            <SectionTitle>
              {width > MOBILE_BREAKPOINT
                ? 'Upgrade to Premium'
                : 'Download with Premium'}
            </SectionTitle>
            <ImpressionWatcher
              source="bulk_download_lookback_modal"
              trackDimensions
              style={{ alignSelf: 'center' }}
            >
              {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
              <Button
                as="a"
                href={checkoutUrl}
                onClick={handleCtaClick}
                premium
              >
                {trialPricingCopy ? (
                  <>
                    <span>{trialPricingCopy}</span>{' '}
                    <span>and Download Your PDF Pack</span>
                  </>
                ) : (
                  'Upgrade to Download Your PDF Pack'
                )}
                <IconContainer>
                  <Icon name="keyboard_arrow_right" size={24} />
                </IconContainer>
              </Button>
            </ImpressionWatcher>

            <FeatureContainer>
              <FeatureIcon src={$image_url('paper-with-pdf-ribbon.png')} />
              {/* @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call. */}
              <Feature noDesc>Download this and any other PDF</Feature>
            </FeatureContainer>

            <FeatureContainer>
              <FeatureIcon src={$image_url('bulk-download-shine.svg')} />
              <Feature>Unlimited PDF Packs</Feature>
              <FeatureDesc>
                Download PDF Packs for this and any other paper
              </FeatureDesc>
            </FeatureContainer>

            <FeatureContainer>
              <FeatureIcon src={$image_url('summary-new.svg')} />
              <Feature>Unlimited Summary Packs</Feature>
              <FeatureDesc>
                Summaries of all the best related papers
              </FeatureDesc>
            </FeatureContainer>

            <FeatureContainer>
              <FeatureIcon src={$image_url('advanced-search.svg')} />
              <Feature>Unlimited Advanced Searching</Feature>
              <FeatureDesc>Full-text search + advanced filtering</FeatureDesc>
            </FeatureContainer>

            <PlusText>Plus 12 other amazing tools for academics</PlusText>
          </PremiumContainer>
        </Container>
      </ImpressionWatcher>
    </StyledModal>
  );
};

export default FallbackBulkDownloadUpsellModalControl;
